import React, { useMemo, useCallback, useEffect, forwardRef } from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import toLower from 'lodash/toLower'

import modelSchema from '@galo-monitor/forms/schemas/root/model.schema'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { ModelActions } from '@galo-monitor/stores/model'
import Form from '@galo-monitor/web-components/Form'
import InputSelect from '@galo-monitor/web-components/InputSelect'
import InputText from '@galo-monitor/web-components/InputText'

import { Container } from './theme'

const ModelForm = forwardRef((props, formRef) => {
  const { model, loading, setLoading, onSuccess, onError } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()

  const { data: modelData, isValidating: isValidatingModelData } = useGaloMonitorApi(model.id ? `/admin/machines/models/${ model.id }` : null)
  const { data: machineTypes } = useGaloMonitorApi('/admin/machines/types')
  const { data: brands } = useGaloMonitorApi('/admin/machines/makers')

  const machineTypeOptions = useMemo(
    () => map(machineTypes, ({ id, name }) => ({
      label: name,
      value: String(id)
    })),
    [machineTypes]
  )

  const brandOptions = useMemo(
    () => map(brands, ({ name }) => ({
      label: name,
      value: name
    })),
    [brands]
  )

  const handleSubmit = useCallback(
    data => {
      setLoading(true)
      dispatch(ModelActions.saveModel(
        {
          ...model,
          ...data
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('model', { howMany: 1 }),
            action: toLower(t(isEmpty(model) ? 'created' : 'updated', { gender: 'male', howMany: 1 }))
          }))
          onSuccess()
        },
        onError
      ))
    },
    [model, dispatch, onError, onSuccess, setLoading, snackbar, t]
  )

  useEffect(() => {
    if (!isEmpty(modelData)) {
      setLoading(false)
    }
  }, [modelData, setLoading, isValidatingModelData])

  useEffect(() => model.id && setLoading(true), [model.id, setLoading])

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ modelSchema }
      onSubmit={ handleSubmit }
    >
      <Container>
        <InputText
          name="name"
          label={ t('name') }
          defaultValue={ model?.name || '' }
          disabled={ loading }
          fullWidth
        />

        <InputSelect
          name="machine_type_id"
          label={ t('type', { howMany: 1 }) }
          defaultValue={ modelData?.machineTypeId?.toString() || '' }
          options={ machineTypeOptions }
          disabled={ loading }
          fullWidth
        />

        <InputSelect
          name="machine_maker_name"
          label={ t('brand', { howMany: 1 }) }
          options={ brandOptions }
          disabled={ loading }
          defaultValue={ modelData?.machineMaker?.name || '' }
          creatable
          fullWidth
        />
      </Container>
    </Form>
  )
})

ModelForm.propTypes = {
  model: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

ModelForm.defaultProps = {
  model: {},
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default ModelForm
